import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogContent, DialogActions,
  IconButton, CircularProgress, Tooltip, Typography, Box, Stack, Chip, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CloseIcon      from '@mui/icons-material/Close';
import WhatsAppIcon   from '@mui/icons-material/WhatsApp';
import PersonIcon     from '@mui/icons-material/Person';
import PeopleIcon     from '@mui/icons-material/People';
import GroupsIcon     from '@mui/icons-material/Groups';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DoneAllIcon    from '@mui/icons-material/DoneAll';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { toast } from 'react-toastify';
import mensajeriaService            from '../services/mensajeriaService';
import whatsappMicroserviceService  from '../services/whatsappMicroserviceService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TEMPLATE_KEY_MAP = {
  'Turno Médico':       'turnoMedico',
  'Turno Médico ART':   'turnoMedico',
  'Turno ANSES':        'turnoAnses',
  'Junta Médica':       'juntaMedica',
  'Turno Junta Médica': 'juntaMedica',
  'Pericia Médica':     'periciaMediaca',
};

const interpolar = (template, vars) =>
  template
    .replace(/\{nombre\}/g,     vars.nombre     || '')
    .replace(/\{tipoEvento\}/g, vars.tipoEvento  || '')
    .replace(/\{fecha\}/g,      vars.fecha       || '')
    .replace(/\{horaCitacion\}/g,   vars.hora || '')
    .replace(/\{horaTurnoAnses\}/g, vars.horaTurnoAnses || vars.hora || '')
    .replace(/\{hora\}/g,       vars.hora        || '')
    .replace(/\{lugar\}/g,      vars.lugar       || '');

const formatearFechaLocal = (fechaStr) => {
  if (!fechaStr) return '';
  const d = new Date(fechaStr.includes('T') ? fechaStr : `${fechaStr}T00:00:00`);
  if (isNaN(d.getTime())) return fechaStr;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (iso) => {
  if (!iso) return { fecha: '-', hora: '-' };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { fecha: '-', hora: '-' };
  return {
    fecha: d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    hora:  d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
  };
};

const truncar = (texto, max = 80) =>
  texto && texto.length > max ? `${texto.slice(0, max)}…` : (texto || '-');

// ─── Subcomponentes ───────────────────────────────────────────────────────────

function FilaHistorial({ item }) {
  const { fecha, hora } = formatDateTime(item.fechaCreacion);
  const inicial = (item.nombreDestinatario || item.telefonoDestinatario || '?')[0].toUpperCase();

  return (
    <TableRow hover sx={{ '& > td': { borderColor: '#e2e8f0' } }}>
      <TableCell sx={{ width: 52, py: 1.1 }}>
        <Avatar sx={{ width: 30, height: 30, bgcolor: '#dbeafe', color: '#1d4ed8', fontSize: '0.78rem', fontWeight: 700 }}>
          {inicial}
        </Avatar>
      </TableCell>
      <TableCell sx={{ py: 1.1 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }} noWrap>
          {item.nombreDestinatario || item.telefonoDestinatario || 'Destinatario'}
        </Typography>
        <Tooltip title={item.contenido || ''} placement="bottom-start" arrow>
          <Typography sx={{ fontSize: '0.76rem', color: '#64748b', lineHeight: 1.35, cursor: 'default', mt: 0.35 }}>
            {truncar(item.contenido, 90)}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ width: 140, py: 1.1, whiteSpace: 'nowrap' }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: '#64748b' }}>
          {item.leido
            ? <DoneAllIcon sx={{ fontSize: 13, color: '#2563eb' }} />
            : <DoneAllIcon sx={{ fontSize: 13, color: '#94a3b8' }} />}
          <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>{fecha}</Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ width: 92, py: 1.1, whiteSpace: 'nowrap' }}>
        <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>{hora}</Typography>
      </TableCell>
      <TableCell sx={{ width: 96, py: 1.1 }}>
        <Chip
          label={item.leido ? 'Leído' : 'Enviado'}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 700,
            bgcolor: item.leido ? '#dbeafe' : '#e2e8f0',
            color: item.leido ? '#1d4ed8' : '#475569',
          }}
        />
      </TableCell>
    </TableRow>
  );
}

// ─── Botón destinatario ───────────────────────────────────────────────────────

function BtnDestinatario({ value, selected, disabled, onClick, icon: Icon, label, sub }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={() => !disabled && onClick(value)}
      disabled={disabled}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 1.75, py: 1, borderRadius: 2,
        border: selected ? '2px solid #25D366' : '2px solid #e2e8f0',
        bgcolor: selected ? '#f0fdf4' : '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'all 0.15s',
        boxShadow: selected ? '0 0 0 3px #bbf7d0' : 'none',
        outline: 'none',
        '&:hover:not(:disabled)': { borderColor: '#25D366', bgcolor: '#f0fdf4' },
      }}
    >
      <Box sx={{
        width: 32, height: 32, borderRadius: '50%',
        bgcolor: selected ? '#25D366' : '#f1f5f9',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, transition: 'background 0.15s',
      }}>
        <Icon sx={{ fontSize: '1rem', color: selected ? '#fff' : '#64748b' }} />
      </Box>
      <Box sx={{ textAlign: 'left' }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: selected ? '#15803d' : '#334155', lineHeight: 1.2 }}>
          {label}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', lineHeight: 1.2 }}>
            {sub}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

function ModalHistorialWhatsapp({
  open, onClose,
  tramiteId,
  numeroTelefono, nombreCliente,
  fechaTurno, horaTurno, horaTurnoAnses, tipoEvento, lugar,
  contactosAdicionales,
}) {
  const [historial,    setHistorial]    = useState([]);
  const [cargando,     setCargando]     = useState(false);
  const [enviando,     setEnviando]     = useState(false);
  const [destinatario, setDestinatario] = useState('titular');
  const [ultimaSincronizacion, setUltimaSincronizacion] = useState(null);

  const tieneContactos = contactosAdicionales?.length > 0;

  const formatearHoraCorta = (fecha) => fecha.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });

  const cargarHistorial = useCallback(async (silencioso = false) => {
    if (!tramiteId) return;
    if (!silencioso) setCargando(true);
    try {
      const data = await mensajeriaService.historialWhatsApp(tramiteId);
      setHistorial(data || []);
      setUltimaSincronizacion(new Date());
    } catch {
      if (!silencioso) setHistorial([]);
    } finally {
      if (!silencioso) setCargando(false);
    }
  }, [tramiteId]);

  useEffect(() => {
    if (open) {
      cargarHistorial();
      setDestinatario('titular');
    }
  }, [open, cargarHistorial]);

  // Refresco silencioso del historial mientras el modal está abierto
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => cargarHistorial(true), 30000);
    return () => clearInterval(id);
  }, [open, cargarHistorial]);

  const construirMensaje = async () => {
    const fallback = 'Hola {nombre}, te recordamos tu {tipoEvento} el {fecha} a las {hora} en {lugar}.';
    let templateStr = fallback;
    try {
      const config    = await whatsappMicroserviceService.getConfig();
      const templates = config?.templates || {};
      const key       = TEMPLATE_KEY_MAP[tipoEvento] || 'default';
      templateStr     = templates[key] || templates['default'] || fallback;
    } catch { /* usa fallback */ }
    return interpolar(templateStr, {
      nombre:     nombreCliente || 'Cliente',
      tipoEvento: tipoEvento    || '',
      fecha:      formatearFechaLocal(fechaTurno),
      hora:       horaTurno     || '',
      horaTurnoAnses: horaTurnoAnses || '',
      lugar:      lugar         || '',
    });
  };

  const resolverDestinatarios = () => {
    const lista = [];
    if ((destinatario === 'titular' || destinatario === 'ambos') && numeroTelefono)
      lista.push({ phone: String(numeroTelefono).replace(/\D/g, ''), label: nombreCliente || 'Titular' });
    if ((destinatario === 'contacto' || destinatario === 'ambos') && tieneContactos)
      contactosAdicionales.forEach((c) => {
        if (c.nroTelefono) lista.push({ phone: String(c.nroTelefono).replace(/\D/g, ''), label: c.nombre || 'Contacto' });
      });
    return lista;
  };

  const handleEnviar = async () => {
    const destinos = resolverDestinatarios();
    if (destinos.length === 0) { toast.warning('No hay destinatarios con teléfono registrado.'); return; }

    setEnviando(true);
    try {
      const mensaje = await construirMensaje();
      const resultados = await Promise.allSettled(
        destinos.map(({ phone, label }) =>
          whatsappMicroserviceService
            .sendMessage({ phone, message: mensaje, tramiteId: tramiteId || null })
            .then((res) => ({ ok: true,  label, conectado: res?.whatsappConectado !== false }))
            .catch(()   => ({ ok: false, label }))
        )
      );

      const exitosos  = resultados.filter((r) => r.value?.ok).map((r) => r.value.label);
      const fallidos  = resultados.filter((r) => !r.value?.ok).map((r) => r.value.label);
      const conectado = resultados.some((r) => r.value?.conectado);

      if (fallidos.length === 0) {
        if (!conectado) toast.warning('Mensaje encolado, pero WhatsApp no está conectado. Se enviará al reconectar.');
        else            toast.success('Recordatorio enviado correctamente.');
        cargarHistorial();
      } else if (exitosos.length > 0) {
        toast.warning(`Enviado parcialmente. Falló: ${fallidos.join(', ')}.`);
        cargarHistorial();
      } else {
        toast.error('No se pudo enviar el recordatorio. Verificá que WhatsApp esté conectado.');
      }
    } catch {
      toast.error('Error al conectar con el servicio de WhatsApp.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid #dbe3ee',
          boxShadow: 'var(--shadow-lg)',
          background: '#f8fafc',
        },
      }}
    >

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{
        background: 'var(--primary)',
        px: 3, py: 2.25,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <WhatsAppIcon sx={{ color: '#fff', fontSize: '1.4rem' }} />
          </Box>
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
              Recordatorio por WhatsApp
            </Typography>
            {nombreCliente && (
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.78rem', lineHeight: 1.2 }}>
                {nombreCliente}
              </Typography>
            )}
          </Box>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, bgcolor: '#f1f5f9', display: 'flex', flexDirection: 'column', minHeight: 0 }}>

        {/* ── Historial ──────────────────────────────────────────────────────── */}
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2, flexShrink: 0 }}>
          <Box sx={{
            bgcolor: '#fff',
            border: '1px solid #dbe3ee',
            borderRadius: 3,
            p: 2,
            boxShadow: 'var(--shadow-md)',
          }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: '0.95rem', color: '#64748b' }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Historial de envíos
              </Typography>
              {historial.length > 0 && (
                <Chip label={historial.length} size="small" sx={{ height: 18, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#dbeafe', color: '#1d4ed8' }} />
              )}
              {ultimaSincronizacion && (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 'auto', color: '#64748b' }}>
                  <RefreshRoundedIcon sx={{ fontSize: 14, color: '#2563eb' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
                    Auto · {formatearHoraCorta(ultimaSincronizacion)}
                  </Typography>
                </Stack>
              )}
            </Stack>

            {cargando && (
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 2, px: 0.5 }}>
                <CircularProgress size={16} thickness={5} sx={{ color: '#2563eb' }} />
                <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8' }}>Cargando historial...</Typography>
              </Stack>
            )}

            {!cargando && historial.length === 0 && (
              <Box sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                py: 2.5, px: 2, borderRadius: 2,
                bgcolor: '#f8fafc', border: '1px dashed #dbe3ee',
              }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: '2rem', color: '#cbd5e1', mb: 0.5 }} />
                <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8', textAlign: 'center' }}>
                  Aún no se enviaron mensajes para este trámite.
                </Typography>
              </Box>
            )}

            {!cargando && historial.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 250, overflowY: 'auto', borderColor: '#dbe3ee', borderRadius: 2 }}>
                <Table stickyHeader size="small" aria-label="Historial de envíos de WhatsApp">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 52, fontSize: '0.72rem', fontWeight: 700, color: '#64748b', bgcolor: '#f8fafc' }} />
                      <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', bgcolor: '#f8fafc' }}>Destinatario / mensaje</TableCell>
                      <TableCell sx={{ width: 140, fontSize: '0.72rem', fontWeight: 700, color: '#64748b', bgcolor: '#f8fafc' }}>Fecha</TableCell>
                      <TableCell sx={{ width: 92, fontSize: '0.72rem', fontWeight: 700, color: '#64748b', bgcolor: '#f8fafc' }}>Hora</TableCell>
                      <TableCell sx={{ width: 96, fontSize: '0.72rem', fontWeight: 700, color: '#64748b', bgcolor: '#f8fafc' }}>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historial.map((item) => (
                      <FilaHistorial key={item.id} item={item} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>

        {/* ── Divider ────────────────────────────────────────────────────────── */}
        <Box sx={{ mx: 2.5, borderTop: '1px solid #dbe3ee' }} />

        {/* ── Destinatario ───────────────────────────────────────────────────── */}
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
            Enviar recordatorio a
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <BtnDestinatario
              value="titular"
              selected={destinatario === 'titular'}
              disabled={!numeroTelefono}
              onClick={setDestinatario}
              icon={PersonIcon}
              label="Titular"
              sub={nombreCliente || undefined}
            />
            {tieneContactos && (
              <BtnDestinatario
                value="contacto"
                selected={destinatario === 'contacto'}
                disabled={false}
                onClick={setDestinatario}
                icon={PeopleIcon}
                label="Contacto adicional"
                sub={`${contactosAdicionales.length} contacto${contactosAdicionales.length > 1 ? 's' : ''}`}
              />
            )}
            {tieneContactos && (
              <BtnDestinatario
                value="ambos"
                selected={destinatario === 'ambos'}
                disabled={!numeroTelefono}
                onClick={setDestinatario}
                icon={GroupsIcon}
                label="Ambos"
                sub="Titular + contactos"
              />
            )}
          </Stack>
        </Box>
      </DialogContent>

      {/* ── Acciones ─────────────────────────────────────────────────────────── */}
      <DialogActions sx={{ px: 2.5, py: 2, bgcolor: '#fff', borderTop: '1px solid #dbe3ee', gap: 1 }}>
        <button
          className="btn btn-outline-secondary btn-sm rounded-pill px-3"
          onClick={onClose}
          disabled={enviando}
          style={{ fontSize: '0.82rem' }}
        >
          Cerrar
        </button>
        <button
          className="btn btn-sm rounded-pill d-inline-flex align-items-center px-3"
          style={{
            background: enviando ? '#94a3b8' : 'var(--whatsapp-dark)',
            border: 'none', color: 'white', fontSize: '0.82rem', fontWeight: 600,
            boxShadow: enviando ? 'none' : '0 2px 8px rgba(37,211,102,0.4)',
            transition: 'all 0.2s',
          }}
          onClick={handleEnviar}
          disabled={enviando}
        >
          {enviando
            ? <CircularProgress size={14} style={{ color: 'white', marginRight: 6 }} />
            : <WhatsAppIcon style={{ fontSize: '1rem', marginRight: 6 }} />}
          {enviando ? 'Enviando...' : 'Enviar recordatorio'}
        </button>
      </DialogActions>
    </Dialog>
  );
}

ModalHistorialWhatsapp.propTypes = {
  open:                  PropTypes.bool.isRequired,
  onClose:               PropTypes.func.isRequired,
  tramiteId:             PropTypes.number,
  numeroTelefono:        PropTypes.string,
  nombreCliente:         PropTypes.string,
  fechaTurno:            PropTypes.string,
  horaTurno:             PropTypes.string,
  // Turno ANSES: hora en el organismo (horaTurno es la de la citación al estudio).
  horaTurnoAnses:        PropTypes.string,
  tipoEvento:            PropTypes.string,
  lugar:                 PropTypes.string,
  contactosAdicionales:  PropTypes.arrayOf(PropTypes.shape({
    nombre:      PropTypes.string.isRequired,
    nroTelefono: PropTypes.string.isRequired,
    relacion:    PropTypes.string,
  })),
};

ModalHistorialWhatsapp.defaultProps = {
  tramiteId:            null,
  numeroTelefono:       '',
  nombreCliente:        '',
  fechaTurno:           '',
  horaTurno:            '',
  horaTurnoAnses:       '',
  tipoEvento:           '',
  lugar:                '',
  contactosAdicionales: [],
};

FilaHistorial.propTypes  = { item: PropTypes.object.isRequired };
BtnDestinatario.propTypes = {
  value: PropTypes.string.isRequired, selected: PropTypes.bool, disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired, icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired, sub: PropTypes.string,
};
BtnDestinatario.defaultProps = { selected: false, disabled: false, sub: undefined };

export default ModalHistorialWhatsapp;
